<template>
  <div
    id="orphanDevices"
    class="modal fade"
    data-backdrop="true"
    tabindex="-1"
    role="dialog"
    aria-labelledby="orphanDevicesLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 id="orphanDevicesLabel" class="modal-title">Dispositivos Órfãos</h5>
          <button type="button" class="close" data-dismiss="modal">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <div class="modal-body">
          <p class="text-muted">
            Dispositivos criados pelo aplicativo ou por Tags (GT06) sem vínculo,
            ainda sem central, unidade e empresa. Preencha os dados para adotá-los.
          </p>

          <button class="btn btn-primary mb-3" :disabled="isLoading" @click="loadOrphans">
            {{ isLoading ? 'Buscando...' : 'Buscar Dispositivos Órfãos' }}
          </button>

          <div v-if="hasSearched && !orphans.length" class="alert alert-warning mb-0">
            Nenhum dispositivo órfão encontrado.
          </div>

          <div v-if="orphans.length" class="table-responsive">
            <table class="table table-sm">
              <thead>
                <tr>
                  <th>Identificação</th>
                  <th>Descrição</th>
                  <th>Visto por último</th>
                  <th>Central</th>
                  <th>Unidade</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                <tr v-for="device in orphans" :key="device.id">
                  <td>
                    <small class="text-muted d-block">{{ identityLabel(device) }}</small>
                    {{ identityOf(device) }}
                  </td>
                  <td>{{ device.description }}</td>
                  <td>{{ lastSeen(device) }}</td>
                  <td>
                    <select
                      v-model="formByDevice[device.id].center_id"
                      class="form-control form-control-sm"
                      @change="onCenterChange(device.id)"
                    >
                      <option :value="null">Selecione</option>
                      <option v-for="c in centers" :key="c.id" :value="c.id">{{ c.name }}</option>
                    </select>
                  </td>
                  <td>
                    <select
                      v-model="formByDevice[device.id].unit_id"
                      class="form-control form-control-sm"
                      :disabled="!formByDevice[device.id].center_id"
                    >
                      <option :value="null">Selecione</option>
                      <option v-for="u in unitsByDevice[device.id] || []" :key="u.id" :value="u.id">{{ u.name }}</option>
                    </select>
                  </td>
                  <td class="text-nowrap">
                    <button
                      class="btn btn-sm btn-outline-primary mr-1"
                      :disabled="!device.last_update"
                      @click="track(device)"
                    >
                      Rastrear
                    </button>
                    <button
                      class="btn btn-sm btn-success"
                      :disabled="!isComplete(device.id) || adoptingByDevice[device.id]"
                      @click="adopt(device)"
                    >
                      {{ adoptingByDevice[device.id] ? 'Adotando...' : 'Adotar' }}
                    </button>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="tracking" class="mt-3">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <strong>Rastreando {{ identityOf(tracking) }}</strong>
              <small class="text-muted">
                {{ trackPath.length }} posição(ões) nas últimas 24 h · {{ lastSeen(tracking) }}
              </small>
            </div>
            <GmapMap
              :center="trackCenter"
              :zoom="15"
              style="width: 100%; height: 360px"
            >
              <GmapPolyline
                :path="trackPath"
                :options="{ strokeColor: '#4169e1', strokeOpacity: 0.9, strokeWeight: 4 }"
              />
              <GmapMarker :position="trackCenter" />
            </GmapMap>
          </div>
        </div>

        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-dismiss="modal">Fechar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import moment from 'moment'

export default {
  name: 'OrphanDevicesModal',
  props: {
    centers: { type: Array, default: () => [] },
  },
  data() {
    return {
      orphans: [],
      formByDevice: {},
      unitsByDevice: {},
      adoptingByDevice: {},
      isLoading: false,
      hasSearched: false,
      tracking: null,
      trackPath: [],
      echoListening: false,
    }
  },
  computed: {
    trackCenter() {
      const last = this.trackPath[this.trackPath.length - 1]
      if (last) return last
      const update = this.tracking && this.tracking.last_update
      return update ? { lat: Number(update.lat), lng: Number(update.long) } : { lat: 0, lng: 0 }
    },
  },
  beforeDestroy() {
    this.stopListening()
  },
  methods: {
    identityLabel(device) {
      if (device.imei) return 'IMEI'
      if (device.mac_address) return 'MAC'
      return 'Android ID'
    },
    identityOf(device) {
      return device.imei || device.mac_address || device.android_id || `#${device.id}`
    },
    lastSeen(device) {
      const at = device.last_update && device.last_update.last_tracking_at
      return at ? moment(at).format('DD/MM/YYYY HH:mm:ss') : 'sem posição'
    },
    async track(device) {
      this.tracking = device
      this.trackPath = []
      try {
        const api = `${this.$store.state.api}devices/${device.id}/tracker-history`
        const response = await axios.get(api, {
          params: { from: moment().subtract(24, 'hours').format('YYYY-MM-DD HH:mm:ss') },
        })
        this.trackPath = (response.data.data || []).map(p => ({
          lat: Number(p.latitude),
          lng: Number(p.longitude),
        }))
      } catch (error) {
        this.$error(error)
      }
      this.listen()
    },
    listen() {
      if (this.echoListening || !window.Echo || !window.Echo.channel) return
      window.Echo.channel('device-last-update').listen('.device-last-update', this.onDevicePosition)
      this.echoListening = true
    },
    stopListening() {
      if (!this.echoListening || !window.Echo || !window.Echo.channel) return
      window.Echo.channel('device-last-update').stopListening('.device-last-update', this.onDevicePosition)
      this.echoListening = false
    },
    onDevicePosition(event) {
      const orphan = this.orphans.find(item => item.id === Number(event.device_id))
      if (!orphan) return
      this.$set(orphan, 'last_update', {
        ...(orphan.last_update || {}),
        lat: event.lat,
        long: event.long,
        speed: event.speed,
        last_tracking_at: event.last_tracking_at,
      })
      if (this.tracking && this.tracking.id === orphan.id) {
        this.trackPath.push({ lat: Number(event.lat), lng: Number(event.long) })
      }
    },
    async loadOrphans() {
      this.isLoading = true
      try {
        const api = `${this.$store.state.api}devices/orphans`
        const response = await axios.get(api)
        this.orphans = response.data.data || []
        this.formByDevice = {}
        this.unitsByDevice = {}
        this.orphans.forEach(device => {
          this.$set(this.formByDevice, device.id, {
            center_id: null,
            unit_id: null,
          })
        })
      } catch (error) {
        this.$error(error)
      } finally {
        this.isLoading = false
        this.hasSearched = true
      }
    },
    async onCenterChange(deviceId) {
      this.formByDevice[deviceId].unit_id = null
      const centerId = this.formByDevice[deviceId].center_id
      if (!centerId) {
        this.$set(this.unitsByDevice, deviceId, [])
        return
      }
      try {
        const api = `${this.$store.state.api}centers/unit/${centerId}`
        const response = await axios.get(api)
        this.$set(this.unitsByDevice, deviceId, response.data || [])
      } catch (error) {
        this.$error(error)
      }
    },
    isComplete(deviceId) {
      const form = this.formByDevice[deviceId] || {}
      // only require center and unit for adoption; supplier/model are optional
      return form.center_id && form.unit_id
    },
    async adopt(device) {
      this.$set(this.adoptingByDevice, device.id, true)
      try {
        const api = `${this.$store.state.api}devices/${device.id}/adopt`
        await axios.put(api, this.formByDevice[device.id])
        this.orphans = this.orphans.filter(item => item.id !== device.id)
        this.$message(
          this.$t('Success'),
          'Dispositivo adotado com sucesso.',
          'success',
        )
        this.$emit('adopted')
      } catch (error) {
        this.$error(error)
      } finally {
        this.$set(this.adoptingByDevice, device.id, false)
      }
    },
  },
}
</script>
